import { indentWithTab } from '@codemirror/commands';
import { EditorState } from '@codemirror/state';
import { EditorView, keymap } from '@codemirror/view';
import { useElementHover } from '@vueuse/core';
import { basicSetup, EditorView as EditorView$1 } from 'codemirror';
import { defineComponent, ref } from 'vue-demi';
import './OrButton-bc9cc4a5.js';
import { s as script$1 } from './OrIconButton-5ccfcc6f.js';
import { s as script$4 } from './OrError-f90296db.js';
import { s as script$5 } from './OrHint-8822005b.js';
import { s as script$6 } from './OrLabel-a7d99df4.js';
import { s as script$2 } from './OrModal-6060ddf4.js';
import { s as script$3 } from './OrTeleport.vue3-9bce0c02.js';
import { html } from '@codemirror/lang-html';
import { css } from '@codemirror/lang-css';
import { json } from '@codemirror/lang-json';
import { javascript } from '@codemirror/lang-javascript';
import { markdown } from '@codemirror/lang-markdown';
import '@codemirror/lint';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags } from '@lezer/highlight';
import { resolveComponent, openBlock, createElementBlock, createVNode, withCtx, renderSlot, createElementVNode, normalizeClass, createBlock, createCommentVNode, withDirectives, vShow } from 'vue';
import { s as styleInject } from './style-inject.es-4c6f2515.js';

var OrCodeLanguages;
(function (OrCodeLanguages) {
  OrCodeLanguages["JS"] = "javascript";
  OrCodeLanguages["TS"] = "typescript";
  OrCodeLanguages["CSS"] = "css";
  OrCodeLanguages["HTML"] = "html";
  OrCodeLanguages["JSON"] = "json";
  OrCodeLanguages["MD"] = "markdown";
})(OrCodeLanguages || (OrCodeLanguages = {}));

const JS = javascript({
  typescript: false
});
const TS = javascript({
  typescript: true
});
const CSS = css();
const HTML = html();
const JSON = json();
const MD = markdown();
var languages = {
  [OrCodeLanguages.JS]: JS,
  [OrCodeLanguages.TS]: TS,
  [OrCodeLanguages.CSS]: CSS,
  [OrCodeLanguages.HTML]: HTML,
  [OrCodeLanguages.JSON]: JSON,
  [OrCodeLanguages.MD]: MD
};

// Using https://github.com/one-dark/vscode-one-dark-theme/ as reference for the colors
const chalky = 'var(--c-warning-darken-20)',
  coral = 'var(--c-error)',
  cyan = '#56b6c2',
  invalid = 'var(--c-white)',
  ivory = 'var(--c-neutral-6)',
  stone = 'var(--c-neutral-5)',
  // Brightened compared to original to increase contrast
  malibu = '#61afef',
  sage = 'var(--c-success)',
  whiskey = 'var(--c-warning-darken-5)',
  violet = '#c678dd',
  darkBackground = 'var(--c-neutral-1)',
  highlightBackground = 'var(--c-neutral-2)',
  background = 'var(--c-neutral-1)',
  tooltipBackground = 'var(--c-neutral-2)',
  selection = 'var(--c-neutral-3)',
  cursor = 'var(--c-primary)';
/// The editor theme styles for One Dark.
const orCodeTheme = EditorView.theme({
  '&': {
    color: ivory,
    backgroundColor: background
  },
  '.cm-content': {
    caretColor: cursor
  },
  '.cm-button': {
    background: highlightBackground,
    color: 'var(--c-neutral-6)',
    border: 0,
    borderRadius: 'var(--s-2)'
  },
  '.cm-panel.cm-search input[type=checkbox]': {
    display: 'inline-block',
    verticalAlign: 'middle'
  },
  '.cm-cursor, .cm-dropCursor': {
    borderLeftColor: cursor
  },
  '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection': {
    backgroundColor: selection
  },
  '.cm-panels': {
    backgroundColor: darkBackground,
    color: ivory
  },
  '.cm-panels.cm-panels-top': {
    borderBottom: '2px solid var(--c-neutral-2)'
  },
  '.cm-panels.cm-panels-bottom': {
    borderTop: '2px solid var(--c-neutral-2)'
  },
  '.cm-searchMatch': {
    backgroundColor: 'var(--c-neutral-1)',
    outline: '1px solid var(--c-neutral-2)'
  },
  '.cm-searchMatch.cm-searchMatch-selected': {
    backgroundColor: '#6199ff2f'
  },
  '.cm-activeLine': {
    backgroundColor: '#6699ff0b'
  },
  '.cm-selectionMatch': {
    backgroundColor: '#aafe661a'
  },
  '&.cm-focused .cm-matchingBracket, &.cm-focused .cm-nonmatchingBracket': {
    backgroundColor: '#bad0f847'
  },
  '.cm-gutters': {
    backgroundColor: background,
    color: stone,
    border: 'none'
  },
  '.cm-activeLineGutter': {
    backgroundColor: highlightBackground
  },
  '.cm-foldPlaceholder': {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#ddd'
  },
  '.cm-tooltip': {
    border: 'none',
    backgroundColor: tooltipBackground
  },
  '.cm-tooltip .cm-tooltip-arrow:before': {
    borderTopColor: 'transparent',
    borderBottomColor: 'transparent'
  },
  '.cm-tooltip .cm-tooltip-arrow:after': {
    borderTopColor: tooltipBackground,
    borderBottomColor: tooltipBackground
  },
  '.cm-tooltip-autocomplete': {
    '& > ul > li[aria-selected]': {
      backgroundColor: highlightBackground,
      color: ivory
    }
  },
  '.cm-textfield': {
    outline: 'none',
    background: background,
    color: 'var(--c-neutral-6)',
    backgroundColor: 'var(--c-neutral-1)',
    border: '1px solid var(--c-neutral-2)',
    borderRadius: 'var(--s-2)'
  }
}, {
  dark: true
});
/// The highlighting style for code in the One Dark theme.
const orCodeHighlightStyle = HighlightStyle.define([{
  tag: tags.keyword,
  color: violet
}, {
  tag: [tags.name, tags.deleted, tags.character, tags.propertyName, tags.macroName],
  color: coral
}, {
  tag: [tags.function(tags.variableName), tags.labelName],
  color: malibu
}, {
  tag: [tags.color, tags.constant(tags.name), tags.standard(tags.name)],
  color: whiskey
}, {
  tag: [tags.definition(tags.name), tags.separator],
  color: ivory
}, {
  tag: [tags.typeName, tags.className, tags.number, tags.changed, tags.annotation, tags.modifier, tags.self, tags.namespace],
  color: chalky
}, {
  tag: [tags.operator, tags.operatorKeyword, tags.url, tags.escape, tags.regexp, tags.link, tags.special(tags.string)],
  color: cyan
}, {
  tag: [tags.meta, tags.comment],
  color: stone
}, {
  tag: tags.strong,
  fontWeight: 'bold'
}, {
  tag: tags.emphasis,
  fontStyle: 'italic'
}, {
  tag: tags.strikethrough,
  textDecoration: 'line-through'
}, {
  tag: tags.link,
  color: stone,
  textDecoration: 'underline'
}, {
  tag: tags.heading,
  fontWeight: 'bold',
  color: coral
}, {
  tag: [tags.atom, tags.bool, tags.special(tags.variableName)],
  color: whiskey
}, {
  tag: [tags.processingInstruction, tags.string, tags.inserted],
  color: sage
}, {
  tag: tags.invalid,
  color: invalid
}]);
/// Extension to enable the One Dark theme (both the editor theme and
/// the highlight style).
const theme = [orCodeTheme, syntaxHighlighting(orCodeHighlightStyle)];

//partially based on https://github.com/logue/vue-codemirror6/blob/master/src/components/CodeMirror.vue
var script = defineComponent({
  name: 'OrCode',
  components: {
    OrIconButton: script$1,
    OrModal: script$2,
    OrTeleport: script$3,
    OrError: script$4,
    OrHint: script$5,
    OrLabel: script$6
  },
  model: {
    prop: 'modelValue',
    event: 'update:modelValue'
  },
  props: {
    label: {
      type: String,
      default: '',
      required: false
    },
    modelValue: {
      type: String,
      default: '',
      required: false
    },
    language: {
      type: [Object, String],
      required: false,
      default: () => OrCodeLanguages.JS
    },
    isReadonly: {
      type: Boolean,
      required: false,
      default: false
    },
    extensions: {
      type: Array,
      required: false,
      default: () => [basicSetup]
    },
    phrases: {
      type: Object,
      required: false,
      default: null
    },
    isShowingFullscreen: {
      type: Boolean,
      default: true,
      required: false
    },
    isInvalid: {
      type: Boolean,
      default: false,
      required: false
    },
    error: {
      type: String,
      default: '',
      required: false
    },
    hint: {
      type: String,
      default: '',
      required: false
    },
    allowPaste: {
      type: Boolean,
      default: true
    },
    allowDrop: {
      type: Boolean,
      default: true
    },
    lineWrapping: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'focus', 'blur'],
  setup(props, {
    emit
  }) {
    // editor config
    const root = ref();
    const target = ref();
    const fullscreenTarget = ref();
    const isHovered = useElementHover(root);
    const editor = new EditorView$1({
      state: EditorState.create({
        doc: props.modelValue
      }),
      dispatch: tr => {
        editor.update([tr]);
        if (tr.changes.empty) return;
        emit('update:modelValue', editor.state.doc.toString());
      }
    });
    return {
      root,
      target,
      fullscreenTarget,
      editor,
      isHovered
    };
  },
  data() {
    return {
      isFullscreen: false
    };
  },
  computed: {
    localLanguage() {
      if (Object.values(OrCodeLanguages).includes(this.language)) {
        return languages[this.language];
      } else {
        return this.language;
      }
    },
    localExtensions() {
      const {
        allowDrop,
        allowPaste,
        lineWrapping,
        extensions
      } = this;
      let ext = [theme, EditorView$1.baseTheme({}),
      // locale settings
      this.phrases ? EditorState.phrases.of(this.phrases) : undefined,
      // Parser language setting
      this.localLanguage,
      // Editable option
      EditorView$1.editable.of(!this.isReadonly),
      // Focus and blur listener
      EditorView$1.updateListener.of(viewUpdate => {
        if (viewUpdate.focusChanged) {
          viewUpdate.view.hasFocus ? this.$emit('focus') : this.$emit('blur', viewUpdate.state.doc.toString());
        }
      }), EditorView$1.domEventHandlers({
        drop(event) {
          if (!allowDrop) {
            event.preventDefault();
            return true;
          }
        },
        paste(event) {
          if (!allowPaste) {
            event.preventDefault();
            return true;
          }
        }
      }),
      // line wrapping
      lineWrapping ? EditorView$1.lineWrapping : undefined, keymap.of([indentWithTab])];
      if (Array.isArray(extensions)) {
        ext = [...ext, ...extensions];
      }
      ext = ext.filter(Boolean);
      return ext;
    },
    isShowingError() {
      return !!this.error && this.isInvalid;
    },
    isShowingHint() {
      return !!this.hint && !this.isShowingError;
    },
    isShowingHeader() {
      var _a;
      return this.isShowingFullscreen || this.$slots.label || this.$slots.actions || ((_a = this.$scopedSlots) === null || _a === void 0 ? void 0 : _a.actions);
    }
  },
  watch: {
    modelValue(value) {
      if (this.editor.composing) return;
      if (this.editor.state.doc.toString() === value) return;
      const previous = this.editor.state.selection;
      this.editor.setState(EditorState.create({
        doc: value,
        extensions: this.localExtensions,
        selection: previous.ranges.some(range => range.to > value.length) ? {
          anchor: value.length
        } : previous
      }));
    },
    isFullscreen(value) {
      if (value) {
        this.fullscreenTarget.append(this.editor.dom);
      } else {
        this.target.append(this.editor.dom);
      }
    },
    localExtensions() {
      this.updateEditorState();
    }
  },
  mounted() {
    this.updateEditorState();
    this.target.append(this.editor.dom);
  },
  beforeUnmount() {
    var _a;
    (_a = this.editor) === null || _a === void 0 ? void 0 : _a.destroy();
  },
  methods: {
    updateEditorState() {
      this.editor.setState(EditorState.create({
        doc: this.modelValue,
        extensions: this.localExtensions
      }));
    },
    toggleFullscreen(value) {
      this.isFullscreen = value === undefined ? !this.isFullscreen : value;
    }
  }
});

const _hoisted_1 = {
  ref: 'root',
  class: "or-code"
};
const _hoisted_2 = {
  key: 0,
  class: "header"
};
const _hoisted_3 = {
  class: "footer"
};
const _hoisted_4 = {
  key: 0,
  class: "header"
};
const _hoisted_5 = {
  class: "actions is-visible"
};
function render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_or_label = resolveComponent("or-label");
  const _component_or_icon_button = resolveComponent("or-icon-button");
  const _component_or_error = resolveComponent("or-error");
  const _component_or_hint = resolveComponent("or-hint");
  const _component_or_modal = resolveComponent("or-modal");
  const _component_or_teleport = resolveComponent("or-teleport");
  return openBlock(), createElementBlock("div", _hoisted_1, [_ctx.isShowingHeader ? (openBlock(), createElementBlock("div", _hoisted_2, [createVNode(_component_or_label, {
    label: _ctx.label
  }, {
    append: withCtx(() => [renderSlot(_ctx.$slots, "label-append")]),
    default: withCtx(() => [renderSlot(_ctx.$slots, "label")]),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["label"]), createElementVNode("div", {
    class: normalizeClass(["actions", {
      'is-visible': _ctx.isHovered
    }])
  }, [renderSlot(_ctx.$slots, "actions", {
    toggleFullscreen: _ctx.toggleFullscreen
  }, () => [_ctx.isShowingFullscreen ? (openBlock(), createBlock(_component_or_icon_button, {
    key: 0,
    icon: "fullscreen",
    "icon-color": "neutral-4",
    class: "fullscreen-button",
    onClick: _cache[0] || (_cache[0] = $event => _ctx.toggleFullscreen(true))
  })) : createCommentVNode("v-if", true)])], 2 /* CLASS */)])) : createCommentVNode("v-if", true), createElementVNode("div", {
    ref: "target",
    class: normalizeClass(["or-code-editor", {
      'is-invalid': _ctx.isInvalid
    }])
  }, null, 2 /* CLASS */), createElementVNode("div", _hoisted_3, [_ctx.isShowingError ? (openBlock(), createBlock(_component_or_error, {
    key: 0,
    error: _ctx.error,
    class: "or-code-error"
  }, null, 8 /* PROPS */, ["error"])) : createCommentVNode("v-if", true), _ctx.isShowingHint ? (openBlock(), createBlock(_component_or_hint, {
    key: 1,
    hint: _ctx.hint,
    class: "or-code-hint"
  }, null, 8 /* PROPS */, ["hint"])) : createCommentVNode("v-if", true)]), createVNode(_component_or_teleport, null, {
    default: withCtx(() => [withDirectives(createVNode(_component_or_modal, {
      "title-text": _ctx.label,
      size: "fullScreen",
      "is-open": "",
      "trap-focus": false,
      onClose: _cache[1] || (_cache[1] = $event => _ctx.toggleFullscreen(false))
    }, {
      default: withCtx(() => [_ctx.$slots['modal-actions'] ? (openBlock(), createElementBlock("div", _hoisted_4, [createElementVNode("div", _hoisted_5, [renderSlot(_ctx.$slots, "modal-actions")])])) : createCommentVNode("v-if", true), createElementVNode("div", {
        ref: "fullscreenTarget",
        class: normalizeClass(["or-code-editor", {
          'is-invalid': _ctx.isInvalid
        }])
      }, null, 2 /* CLASS */), _ctx.isShowingError ? (openBlock(), createBlock(_component_or_error, {
        key: 1,
        error: _ctx.error,
        class: "or-code-error"
      }, null, 8 /* PROPS */, ["error"])) : createCommentVNode("v-if", true)]),
      _: 3 /* FORWARDED */
    }, 8 /* PROPS */, ["title-text"]), [[vShow, _ctx.isFullscreen]])]),
    _: 3 /* FORWARDED */
  })]);
}

var css_248z = "/**\n * A simple way to manage mobile breakpoints described in https://davidwalsh.name/write-media-queries-sass.\n * Can be further extended using following approach https://rimdev.io/making-media-query-mixins-with-sass/\n */\n@keyframes spinAround-3d4b8218 {\nfrom {\n    transform: rotate(0);\n}\nto {\n    transform: rotate(359deg);\n}\n}\n@keyframes fade-3d4b8218 {\nfrom {\n    opacity: 1;\n}\nto {\n    opacity: 0;\n}\n}\n.or-code .fullscreen-button[data-v-3d4b8218] {\n  width: 2.25rem;\n  height: 2.25rem;\n}\n.or-code .fullscreen-button > .or-icon[data-v-3d4b8218] {\n  font-size: 1.5rem;\n}\n.or-code .footer .or-code-hint[data-v-3d4b8218],\n.or-code .footer .or-code-error[data-v-3d4b8218] {\n  flex-shrink: 1;\n  transition-timing-function: var(--t-func);\n  transition-duration: var(--t-duration);\n  transition-property: height, opacity;\n}\n.header[data-v-3d4b8218] {\n  display: flex;\n  flex-direction: row;\n  align-items: center;\n  height: 36px;\n}\n.header .actions[data-v-3d4b8218] {\n  margin-left: auto;\n}\n.header .actions[data-v-3d4b8218]:not(.is-visible) {\n  display: none;\n}\n.or-code-editor[data-v-3d4b8218] {\n  min-height: calc(1.4em + 8px);\n  overflow: auto;\n  font-size: var(--fs-0);\n  border: 1px solid var(--c-neutral-2);\n  border-radius: var(--s-2);\n}\n.or-code-editor.is-invalid[data-v-3d4b8218] {\n  border-color: var(--c-error);\n}\n.or-code-editor[data-v-3d4b8218] >  * {\n  width: 100%;\n  height: 100%;\n}\n.or-code[data-v-3d4b8218],\n.or-modal[data-v-3d4b8218] .content {\n  display: flex;\n  flex-direction: column;\n}\n.or-code .or-code-editor[data-v-3d4b8218],\n.or-modal[data-v-3d4b8218] .content .or-code-editor {\n  flex-grow: 1;\n  text-align: left !important;\n}";
styleInject(css_248z);

script.render = render;
script.__scopeId = "data-v-3d4b8218";
script.__file = "src/components/or-code/OrCode.vue";

export { OrCodeLanguages as O, script as s };
